// QueryClientProvider + live invalidation from the Socket.IO event stream.
//
// Mounted once, above the router (web/src/App.jsx). The QueryClient is created
// in a ref so React 18 StrictMode's double-invoke and any App re-render can
// never swap the cache out from under mounted queries.

import React, { useEffect, useRef } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { socket } from '@platform/http/socket';
import { client } from '@platform/http/api-client';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { createQueryClient } from './queryClient';
import { qk } from './queryKeys';
import { affectsReferenceData } from './policy';

export function QueryProvider({ children }) {
  const clientRef = useRef(null);
  if (!clientRef.current) clientRef.current = createQueryClient();
  const queryClient = clientRef.current;

  // Identity changes must empty the cache. Logout is the obvious case, but
  // IMPERSONATION is the one that actually matters: startImpersonation swaps
  // the token for a different user's, and several reference endpoints are
  // permission-filtered (/api/projects most notably). Carrying the admin's
  // cached results into the impersonated session would show that user data
  // their own permissions exclude. Keyed on the token because that is what
  // changes on login, logout, impersonation start AND end.
  const { token } = useAuth();
  const prevTokenRef = useRef(token);
  useEffect(() => {
    if (prevTokenRef.current === token) return;   // first mount / no change
    prevTokenRef.current = token;
    queryClient.clear();
  }, [token, queryClient]);

  useEffect(() => {
    // Work-item mutations arrive as 'issues-changed'. Invalidate ONLY the
    // work-item namespace: reference data (fields, users, statuses) is
    // unaffected by an issue edit, and invalidating it here would undo the
    // caching this whole layer exists to provide.
    //
    // We deliberately DO NOT call isDuplicateIssuesChangedEvent(). That
    // helper is module-level first-caller-wins state, so a second consumer
    // steals events from the first — IssueScreenPanel.jsx:572 documents
    // getting bitten by exactly this (Structure.jsx mounts earlier and
    // claimed every event). invalidateQueries is idempotent and React Query
    // coalesces concurrent refetches per key, so a duplicate delivery costs
    // nothing and needs no dedup.
    //
    // invalidateQueries only refetches queries that are currently MOUNTED
    // and active; unmounted cached entries are just marked stale and refetch
    // lazily on next use. That is what keeps this from reproducing the
    // reconnect refetch storm diagnosed in d591327.
    const onIssuesChanged = () => {
      queryClient.invalidateQueries({ queryKey: qk.workItems() });
    };

    socket.on('issues-changed', onIssuesChanged);
    return () => { socket.off('issues-changed', onIssuesChanged); };
  }, [queryClient]);

  // Admin writes must drop the reference cache.
  //
  // Without this, an admin who adds a status or custom field in the Migration
  // console (or a group in the Users & Groups admin) would keep seeing the
  // pre-edit list for up to STALE.REFERENCE — 10 minutes — because nothing
  // else invalidates it. Before the cache existed every mount refetched, so
  // this would be a behaviour REGRESSION rather than merely a missing feature.
  //
  // Done as ONE axios interceptor rather than a call in each admin save
  // handler, deliberately: those handlers live in Settings.jsx, the migration
  // console and UsersGroupsAdmin, and a per-handler approach only stays
  // correct as long as everyone remembers. Here it cannot be forgotten, and
  // no admin surface needs to know the cache exists.
  //
  // We do NOT use useMutation for this — every write in the app is still an
  // imperative api.* call, so the interceptor is the only place that sees all
  // of them.
  useEffect(() => {
    const id = client.interceptors.response.use((response) => {
      const { method, url } = response.config || {};
      if (affectsReferenceData(method, url)) {
        queryClient.invalidateQueries({ queryKey: qk.reference() });
      }
      return response;                       // never swallow the response
    });
    // Failed writes changed nothing, so only the success path invalidates —
    // registering no rejection handler leaves the auth-refresh interceptor in
    // api-client.js as the sole owner of the error path.
    return () => { client.interceptors.response.eject(id); };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}

export default QueryProvider;
